import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useLanguage } from "../lib/language";
import ProductCard from "../components/ProductCard";

// Fallback single-URL proxy (used only until the batch resolves this image).
const proxyImage = (url) => {
    if (!url) return "";
    try {
        const base = api.defaults?.baseURL || "";
        return `${base}/bridge/image?u=${encodeURIComponent(url)}`;
    } catch {
        return url;
    }
};

// Build a short list of page numbers to render with ellipsis separators.
const buildPageList = (current, total) => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages = new Set([1, total, current, current - 1, current + 1]);
    const sorted = [...pages].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);
    const out = [];
    for (let i = 0; i < sorted.length; i++) {
        out.push(sorted[i]);
        if (i < sorted.length - 1 && sorted[i + 1] - sorted[i] > 1) out.push("…");
    }
    return out;
};

const PAGE_SIZE = 24;

export default function Shop() {
    const [searchParams, setSearchParams] = useSearchParams();
    const { t } = useLanguage();

    const [raw, setRaw] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [imageMap, setImageMap] = useState({}); // original URL → renewed URL
    const [batchedPages, setBatchedPages] = useState(() => new Set());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [filters, setFilters] = useState({
        q: searchParams.get("q") || "",
        categoria: searchParams.get("categoria") || "",
        min_price: searchParams.get("min_price") || "",
        max_price: searchParams.get("max_price") || "",
    });
    // Keep the current page in the URL so the browser's back button navigates
    // through pagination history naturally (?page=2 → back → ?page=1).
    const page = Math.max(1, parseInt(searchParams.get("page") || "1", 10) || 1);

    // Sync filters → URL (deep-link friendly). Changing a filter also resets
    // page to 1.
    useEffect(() => {
        const sp = {};
        Object.entries(filters).forEach(([k, v]) => { if (v) sp[k] = v; });
        if (page > 1) sp.page = String(page);
        setSearchParams(sp, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    // Fetch inventory + categorias ONCE. We no longer block the render on image
    // refresh — cards appear immediately with placeholders, and each page
    // batches its own 24 image URLs on demand.
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError("");
        Promise.all([
            api.get("/bridge/inventario").then((r) => r.data?.products || []),
            api.get("/bridge/categorias").then((r) => r.data?.categorias || []),
        ])
            .then(([items, cats]) => {
                if (cancelled) return;
                setRaw(items);
                setCategorias(cats);
            })
            .catch(() => {
                if (cancelled) return;
                setError(t(
                    "No pudimos conectar con el inventario. Reintentando…",
                    "We couldn't reach the inventory. Retrying…",
                ));
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const refetch = () => {
        setError("");
        setLoading(true);
        api.get("/bridge/inventario?force=true")
            .then((r) => setRaw(r.data?.products || []))
            .catch(() => setError(t("Aún no podemos conectar.", "Still can't connect.")))
            .finally(() => setLoading(false));
    };

    const resolveImage = (url) => {
        if (!url) return "";
        return imageMap[url] || proxyImage(url);
    };

    const normalize = (p) => ({
        sku: String(p.id || p.sku || "").trim(),
        nombre: p.nombre || "",
        descripcion: p.descripcion || "",
        categoria: p.categoria || "",
        condicion: p.condicion || "Nuevo",
        precio_venta: Number(p.precio_venta || 0),
        cantidad_disponible: Number(p.cantidad_disponible || 0),
        available: Number(p.cantidad_disponible || 0),
        estado: p.estado || (Number(p.cantidad_disponible || 0) > 0 ? "Disponible" : "Vendido"),
        foto: resolveImage(p.foto),
        images: [],
        sold_count: Number(p.sold_count || 0),
        vendedor_id: null,
    });

    // Client-side filtering: in-stock products that match the query against ID,
    // name, description AND category — case-insensitive. The category dropdown
    // is a separate stricter filter.
    const filtered = useMemo(() => {
        const q = (filters.q || "").trim().toLowerCase();
        const min = filters.min_price ? parseFloat(filters.min_price) : null;
        const max = filters.max_price ? parseFloat(filters.max_price) : null;
        return raw
            .filter((p) => Number(p.cantidad_disponible || 0) > 0)
            .filter((p) => (filters.categoria ? (p.categoria || "") === filters.categoria : true))
            .filter((p) => (min != null ? Number(p.precio_venta || 0) >= min : true))
            .filter((p) => (max != null ? Number(p.precio_venta || 0) <= max : true))
            .filter((p) => {
                if (!q) return true;
                const hay = [p.id, p.sku, p.nombre, p.descripcion, p.categoria]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();
                return hay.includes(q);
            })
            .map(normalize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [raw, filters, imageMap]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages);
    const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

    // Reset page to 1 when filters change (unless the URL already specifies one
    // that's valid for the new filter set).
    useEffect(() => {
        if (page > totalPages) {
            const sp = Object.fromEntries(searchParams);
            delete sp.page;
            setSearchParams(sp, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    // Batch-refresh the images shown on the CURRENT page as soon as it changes.
    // Each page batches at most 24 URLs → fast first paint and we don't flood
    // the bot. Already-batched pages are skipped via `batchedPages`.
    useEffect(() => {
        if (pageItems.length === 0) return;
        if (batchedPages.has(safePage)) return;
        const urls = pageItems
            .map((p) => raw.find((r) => String(r.id || r.sku) === p.sku)?.foto)
            .filter((u) => typeof u === "string" && u && !imageMap[u]);
        if (urls.length === 0) {
            setBatchedPages((prev) => new Set(prev).add(safePage));
            return;
        }
        let cancelled = false;
        api.post("/bridge/images/batch", { urls })
            .then((r) => {
                if (cancelled) return;
                setImageMap((prev) => ({ ...prev, ...(r.data?.mapping || {}) }));
                setBatchedPages((prev) => new Set(prev).add(safePage));
            })
            .catch(() => {
                // Non-fatal: cards will keep showing via the single-URL proxy.
                if (!cancelled) setBatchedPages((prev) => new Set(prev).add(safePage));
            });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [safePage, raw.length, filters]);

    const goToPage = (n) => {
        const clamped = Math.max(1, Math.min(totalPages, n));
        const sp = Object.fromEntries(searchParams);
        if (clamped === 1) delete sp.page;
        else sp.page = String(clamped);
        // push (not replace) so the browser back button navigates to the previous page
        setSearchParams(sp);
        try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch { /* noop */ }
    };

    const pageList = buildPageList(safePage, totalPages);

    return (
        <div className="max-w-7xl mx-auto px-6 py-12 fade-in">
            <div className="mb-12">
                <div className="tb-overline text-[var(--tb-gold)] mb-3">{t("Catálogo", "Catalog")}</div>
                <h1 className="font-display text-4xl sm:text-5xl tracking-tight mb-3">{t("Tienda Terra", "Terra Shop")}</h1>
                <p className="text-[var(--tb-text-2)]">
                    {t("Inventario en vivo desde el almacén. Todos los precios en USD.", "Live inventory from the warehouse. All prices in USD.")}
                </p>
            </div>

            {error && (
                <div className="tb-card p-4 mb-6 flex items-center justify-between" data-testid="shop-error">
                    <span className="text-sm text-[var(--tb-text-2)]">{error}</span>
                    <button className="tb-btn-secondary !text-xs" onClick={refetch} data-testid="shop-retry">
                        {t("Reintentar", "Retry")}
                    </button>
                </div>
            )}

            <div className="grid md:grid-cols-12 gap-8">
                <aside className="md:col-span-3 space-y-6">
                    <div>
                        <div className="tb-overline mb-2 text-[var(--tb-gold)]">{t("Buscar", "Search")}</div>
                        <input
                            className="tb-input"
                            placeholder={t("ID, nombre, categoría o descripción", "ID, name, category or description")}
                            value={filters.q}
                            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                            data-testid="filter-q"
                        />
                    </div>
                    <div>
                        <div className="tb-overline mb-2 text-[var(--tb-gold)]">{t("Categoría", "Category")}</div>
                        <select
                            className="tb-input"
                            value={filters.categoria}
                            onChange={(e) => setFilters({ ...filters, categoria: e.target.value })}
                            data-testid="filter-categoria"
                        >
                            <option value="">{t("Todas", "All")}</option>
                            {categorias.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <div className="tb-overline mb-2 text-[var(--tb-gold)]">{t("Min $", "Min $")}</div>
                            <input
                                type="number"
                                className="tb-input"
                                value={filters.min_price}
                                onChange={(e) => setFilters({ ...filters, min_price: e.target.value })}
                                data-testid="filter-min"
                            />
                        </div>
                        <div>
                            <div className="tb-overline mb-2 text-[var(--tb-gold)]">{t("Max $", "Max $")}</div>
                            <input
                                type="number"
                                className="tb-input"
                                value={filters.max_price}
                                onChange={(e) => setFilters({ ...filters, max_price: e.target.value })}
                                data-testid="filter-max"
                            />
                        </div>
                    </div>
                </aside>

                <div className="md:col-span-9">
                    <div className="text-sm text-[var(--tb-text-2)] mb-4" data-testid="shop-results-count">
                        {loading
                            ? t("Cargando inventario…", "Loading inventory…")
                            : <>{filtered.length} {t("productos", "products")}
                                {totalPages > 1 && (
                                    <span className="ml-2 text-[var(--tb-muted)]">
                                        · {t("Página", "Page")} {safePage} / {totalPages}
                                    </span>
                                )}
                            </>}
                    </div>

                    {loading && (
                        <div className="grid grid-cols-2 lg:grid-cols-3 gap-6" data-testid="shop-skeleton">
                            {Array.from({ length: 6 }).map((_, i) => (
                                <div key={i} className="tb-card overflow-hidden">
                                    <div className="aspect-[4/5] bg-[var(--tb-surface-2)] animate-pulse" />
                                    <div className="p-4 space-y-2">
                                        <div className="h-3 w-20 bg-[var(--tb-surface-2)] animate-pulse rounded" />
                                        <div className="h-4 w-3/4 bg-[var(--tb-surface-2)] animate-pulse rounded" />
                                        <div className="h-4 w-16 bg-[var(--tb-surface-2)] animate-pulse rounded" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {!loading && pageItems.length === 0 && (
                        <div className="tb-card p-12 text-center text-[var(--tb-text-2)]" data-testid="shop-empty">
                            {t("No se encontraron productos.", "No products found.")}
                        </div>
                    )}

                    {!loading && pageItems.length > 0 && (
                        <>
                            <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 stagger">
                                {pageItems.map((p) => <ProductCard key={p.sku} p={p} />)}
                            </div>
                            {totalPages > 1 && (
                                <div className="flex items-center justify-center flex-wrap gap-1.5 mt-10" data-testid="shop-pagination">
                                    {pageList.map((n, i) => (
                                        n === "…" ? (
                                            <span key={`e-${i}`} className="px-2 text-xs text-[var(--tb-muted)]">…</span>
                                        ) : (
                                            <button
                                                key={n}
                                                onClick={() => goToPage(n)}
                                                className={`min-w-[2.25rem] h-9 px-3 text-xs rounded border transition ${n === safePage
                                                    ? "border-[var(--tb-gold)] bg-[var(--tb-border-gold)]/10 text-[var(--tb-gold)]"
                                                    : "border-[var(--tb-border)] text-[var(--tb-text-2)] hover:border-[var(--tb-gold)]/60 hover:text-[var(--tb-gold)]"}`}
                                                data-testid={`shop-page-${n}`}>
                                                {n}
                                            </button>
                                        )
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
